import { deviceType } from 'detect-it';
import { type Logger } from 'loglevel';
import { ServiceTokens } from '../../../container/services';
import { callSessionService } from '../../businessServices';
import { type CommonRealtimeService } from '../realtime/commonRealtime.service';
import {
  Platform,
  PostSessionDTO,
  type VideoProvider,
} from '../../call-session';
import { Vendor } from '../../../webrtc/media';
import { BrowserType } from '@doxyme/ui/hooks';

type RecordCallStartProps = {
  sessionId: string;
  userId: number;
  patients: string[];
};

type RecordCallParticipantsProps = {
  dbSessionId?: string;
  patients: string[];
  userId: number;
};

type RecordCallEndProps = {
  dbSessionId?: string;
  roomId?: string;
};

type CallHistoryServiceDependencies = {
  [ServiceTokens.CommonRealtime]: CommonRealtimeService;
  [ServiceTokens.Logger]: Logger;
};

export class CallHistoryService {
  private readonly realtime: CommonRealtimeService;

  private readonly logger: Logger;

  private sessions: Record<string, PostSessionDTO> = {};

  private _currentDbSessionId: string | undefined;

  constructor({ commonRealtime, logger }: CallHistoryServiceDependencies) {
    this.realtime = commonRealtime;
    this.logger = logger;
  }

  get currentDbSessionId(): string | undefined {
    return this._currentDbSessionId;
  }

  async recordCallStart({
    sessionId,
    userId,
    patients,
  }: RecordCallStartProps): Promise<string> {
    if (patients.length === 0 || !patients[0]) {
      throw new Error('At least one patient is required');
    }

    const firstPatient = this.realtime.getMember(patients[0]);

    if (!firstPatient?.roomId) {
      throw new Error('Room ID is required');
    }

    const doctorPlatform =
      deviceType === 'mouseOnly' ? Platform.Web : Platform.Mobile;
    const patientPlatform =
      firstPatient?.state['system']?.platform === BrowserType.Desktop
        ? Platform.Web
        : Platform.Mobile;

    const sessionPayload = {
      userId: Number(userId),
      channel: sessionId,
      patientPlatform,
      isWeb: true,
      doctorPlatform,
      videoProvider: Vendor.Vonage.toLowerCase() as unknown as VideoProvider,
      participantsCount: patients.length + 1,
      internal: false,
      connectionInfo: [],
      pidList: '',
    };

    const dbSession = await callSessionService.postSession(
      firstPatient.roomId,
      sessionPayload,
    );

    this._currentDbSessionId = dbSession.id;

    await this.recordCallParticipants({
      dbSessionId: dbSession.id,
      patients,
      userId,
    });

    this.sessions[dbSession.id] = sessionPayload;

    return dbSession.id;
  }

  async recordCallParticipants({
    patients,
    userId,
    dbSessionId = this.currentDbSessionId,
  }: RecordCallParticipantsProps): Promise<void> {
    if (!dbSessionId) {
      this.logger.error('DB session ID is required');
      return;
    }

    const session = this.sessions[dbSessionId];
    if (session) {
      session.participantsCount += patients.length;
    }

    for (const patient of patients) {
      const member = this.realtime.getMember(patient);
      const sessionParticipant = {
        userId: Number(userId),
        waitTime: member?.state['checkedInAt']
          ? Date.now() - (member?.state['checkedInAt'] as number)
          : 0,
        participantUuid: patient,
      };
      callSessionService.postSessionParticipants(
        dbSessionId,
        sessionParticipant,
      );
    }
  }

  async recordCallEnd(
    { dbSessionId, roomId }: RecordCallEndProps = {
      dbSessionId: this.currentDbSessionId,
    },
  ): Promise<void> {
    if (!dbSessionId) {
      this.logger.error('DB session ID is required');

      return;
    }

    await callSessionService.postSessionEnd(
      dbSessionId,
      this.sessions[dbSessionId]?.participantsCount,
      roomId,
    );

    delete this.sessions[dbSessionId];

    if (this.currentDbSessionId === dbSessionId) {
      this._currentDbSessionId = undefined;
    }
  }
}
